import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react'
import {
  getTagsForBuilding,
  addTagToBuilding,
  removeTagFromBuilding,
  getAllTags,
  createTag,
} from '../../../../../services/tagsService'
import type { BuildingTagWithDetails, Tag } from '@popety-io/types'
import { useAppDispatch } from '@/store/hooks'
import { getTagsWithBuildings } from '@/store/slices/tagsSlice'

interface BuildingTagsContextType {
  tags: BuildingTagWithDetails[]
  allTags: Tag[]
  loading: boolean
  error: string | null
  isEditing: boolean
  setIsEditing: (editing: boolean) => void
  fetchTags: (buildingId: string | number) => Promise<void>
  refreshTags: () => Promise<void>
  clearTags: () => void
  addTag: (tagId: string, isPrimary: boolean) => Promise<void>
  removeTag: (tagId: string) => Promise<void>
  createNewTag: (name: string, color: string) => Promise<Tag>
  loadAllTags: () => Promise<void>
}

const BuildingTagsContext = createContext<BuildingTagsContextType | undefined>(
  undefined
)

export const BuildingTagsProvider: React.FC<{
  children: React.ReactNode
  buildingId?: string
}> = ({ children, buildingId }) => {
  const dispatch = useAppDispatch()
  const [tags, setTags] = useState<BuildingTagWithDetails[]>([])
  const [allTags, setAllTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [currentBuildingId, setCurrentBuildingId] = useState<string | null>(
    null
  )

  const fetchTags = useCallback(async (buildingId: string | number) => {
    setLoading(true)
    setError(null)
    setCurrentBuildingId(buildingId.toString())
    try {
      const fetchedTags = await getTagsForBuilding(buildingId.toString())
      setTags(fetchedTags)
    } catch (err) {
      setError('Failed to fetch building tags')
      console.error('Error fetching building tags:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const refreshTags = useCallback(async () => {
    if (currentBuildingId) {
      await fetchTags(currentBuildingId)
    }
  }, [currentBuildingId, fetchTags])

  const clearTags = useCallback(() => {
    setTags([])
    setError(null)
  }, [])

  const loadAllTags = useCallback(async () => {
    try {
      const allTagsData = await getAllTags()
      setAllTags(allTagsData)
    } catch (err) {
      console.error('Error loading all tags:', err)
      setError('Failed to load tags')
    }
  }, [])

  const addTag = useCallback(
    async (tagId: string, isPrimary: boolean) => {
      if (!currentBuildingId) return

      setLoading(true)
      setError(null)

      try {
        await addTagToBuilding(currentBuildingId, tagId, isPrimary)
        await refreshTags()

        // Refresh Redux store if primary tag was added/changed
        if (isPrimary) {
          dispatch(getTagsWithBuildings())
        }
      } catch (err) {
        console.error('Error adding tag:', err)
        setError('Failed to add tag')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [currentBuildingId, refreshTags, dispatch]
  )

  const removeTag = useCallback(
    async (tagId: string) => {
      if (!currentBuildingId) return

      setLoading(true)
      setError(null)

      try {
        // Check if this is a primary tag
        const tagToRemove = tags.find((t) => t.id === tagId)
        const wasPrimary = tagToRemove?.primary

        await removeTagFromBuilding(tagId)
        await refreshTags()

        // Refresh Redux store if primary tag was removed
        if (wasPrimary) {
          dispatch(getTagsWithBuildings())
        }
      } catch (err) {
        console.error('Error removing tag:', err)
        setError('Failed to remove tag')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [currentBuildingId, tags, refreshTags, dispatch]
  )

  const createNewTag = useCallback(
    async (name: string, color: string): Promise<Tag> => {
      try {
        const newTag = await createTag(name, color)
        // Refresh all tags to include the new one
        await loadAllTags()
        return newTag
      } catch (err) {
        console.error('Error creating tag:', err)
        setError('Failed to create tag')
        throw err
      }
    },
    [loadAllTags]
  )

  // Auto-fetch tags when buildingId changes
  useEffect(() => {
    if (buildingId) {
      fetchTags(buildingId)
    } else {
      clearTags()
    }
  }, [buildingId, fetchTags, clearTags])

  const value: BuildingTagsContextType = {
    tags,
    allTags,
    loading,
    error,
    isEditing,
    setIsEditing,
    fetchTags,
    refreshTags,
    clearTags,
    addTag,
    removeTag,
    createNewTag,
    loadAllTags,
  }

  return (
    <BuildingTagsContext.Provider value={value}>
      {children}
    </BuildingTagsContext.Provider>
  )
}

export const useBuildingTags = () => {
  const context = useContext(BuildingTagsContext)
  if (context === undefined) {
    throw new Error(
      'useBuildingTags must be used within a BuildingTagsProvider'
    )
  }
  return context
}
